import React, { useEffect, useState } from 'react';

type Req = (path: string, body?: any, signal?: AbortSignal) => Promise<any>;

const MIN = 0.5, MAX = 10, STEP = 0.5;
const fmt = (m: number) => (Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1)) + 'x';
const money = (n: number) => '$' + (Math.round(n * 100) / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 });

// Owner kill switch and stake multiplier for bets mirrored from Fady's paper workspace into the live Auto grid.
const FadyMirrorToggle: React.FC<{ request: Req }> = ({ request }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saved, setSaved] = useState<number>(1);
  const [draft, setDraft] = useState<number>(1);
  const [maxStake, setMaxStake] = useState<number>(1000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const apply = (d: any) => {
    setEnabled(!!d.enabled);
    const m = typeof d.multiplier === 'number' ? d.multiplier : 1;
    setSaved(m); setDraft(m);
    if (typeof d.max_stake === 'number') setMaxStake(d.max_stake);
  };
  useEffect(() => {
    const ac = new AbortController();
    request('/api/rolling/simple-bsp/fady-mirror', undefined, ac.signal)
      .then(apply).catch((e: any) => { if (!ac.signal.aborted) setError(e.message); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function post(body: any) {
    setBusy(true); setError('');
    try { apply(await request('/api/rolling/simple-bsp/fady-mirror', body)); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  async function flip() {
    if (enabled === null) return;
    const next = !enabled;
    if (!window.confirm(next
      ? `Mimic Fady? His paper bets (manual and Fady lane, including duplicates of yours) will be placed LIVE from your account at ${fmt(saved)} his stakes.`
      : 'Stop mimicking Fady? Every Fady bet not yet submitted will be withdrawn.')) return;
    await post({ enabled: next });
  }
  async function saveMultiplier() {
    if (draft === saved) return;
    if (draft > saved && !window.confirm(`Scale Fady's bets to ${fmt(draft)}? A $100 Fady bet becomes a ${money(100 * draft)} live bet. Queued Fady bets not yet submitted are re-staked on the next cycle.`)) return;
    await post({ multiplier: draft });
  }
  return (
    <div className="fady-mirror-toggle" role="group" aria-label="Mimic Fady">
      <strong>Mimic Fady</strong>
      <span className={'fady-mirror-state ' + (enabled ? 'on' : 'off')}>{enabled === null ? '…' : enabled ? `ON — live at ${fmt(saved)}` : 'OFF'}</span>
      <button type="button" disabled={busy || enabled === null} onClick={flip}>{enabled ? 'Stop mimicking Fady' : 'Mimic Fady'}</button>
      <label className="fady-mirror-scale" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
        <span>Scale</span>
        <input type="range" min={MIN} max={MAX} step={STEP} value={draft} disabled={busy || enabled === null}
          aria-label="Fady stake multiplier" onChange={e => setDraft(Number(e.target.value))} />
        <output style={{ minWidth: 40, fontWeight: 600 }}>{fmt(draft)}</output>
        <small>Fady $100 → you {money(100 * draft)}</small>
        <button type="button" disabled={busy || enabled === null || draft === saved} onClick={saveMultiplier}>
          {draft === saved ? 'Saved' : `Apply ${fmt(draft)}`}
        </button>
      </label>
      <small>Adds every bet Fady makes (manual and Fady lane, duplicates included) to this live grid at his $ stake × your scale ({fmt(MIN)}–{fmt(MAX)}). Fady paper stakes above {money(maxStake)} are not copied. His stake edits, horse changes and deletions follow until 30s before the jump; a new scale re-stakes queued Fady bets not yet submitted. Off withdraws any not yet submitted. Lays and multiples are not copied.</small>
      {error && <span role="alert">{error}</span>}
    </div>
  );
};
export default FadyMirrorToggle;
